import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  Autocomplete,
  Button,
  Divider,
  Grid,
  Paper,
  TextField,
  Typography,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Stack,
  Tooltip,
  InputAdornment,
  CircularProgress,
} from "@mui/material";
import { RemoveCircleOutline, Search } from "@mui/icons-material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import useFetchObjects from "../../../../api/useFetchObjects";
import useAdd from "../../../../api/useAdd";
import { useAuth } from "../../../../context";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useStockMedicineSearch } from "../../../../hooks/useStockMedicineSearch";

const getCurrentDate = () => new Date().toISOString().split("T")[0];

// Debounce function
function debounce(func, delay) {
  let timeout;
  return function (...args) {
    const context = this;
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(context, args), delay);
  };
}

const FreeMedicine = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [searchTerm, setSearchTerm] = useState("");
  const [allPatients, setAllPatients] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [patientInputValue, setPatientInputValue] = useState("");
  const [forceUpdate, setForceUpdate] = useState(0); // Add a forceUpdate state

  // Use the new backend search hook for medicines
  const {
    suggestions: medicineSuggestions,
    isLoading: isSearchingMedicines,
    quickSearch: searchMedicines,
    clearResults: clearMedicineResults,
  } = useStockMedicineSearch();

  // Legacy medicine loading (fallback) - DISABLED: Now using backend search only
  // const { data: stockMedicines, isLoading: isLoadingMedicines } =
  //   useFetchObjects(["stock-medicines"], `stock-medicines/`, token);

  const {
    handleAdd: handleAddOrder,
    resetForm,
    loading,
  } = useAdd(
    "medicine-order-free",
    token,
    t("pharmacy.medicineOrderCreatedSuccess"),
    t("pharmacy.failedToCreateOrder")
  );

  const {
    data: patientsData,
    isLoading: isLoadingPatients,
    refetch,
  } = useFetchObjects(
    ["patients", page, pageSize, searchTerm, forceUpdate], // Include forceUpdate
    `patients/?page=${page}&page_size=${pageSize}&search=${searchTerm}`,
    token
  );

  useEffect(() => {
    if (patientsData?.results) {
      setAllPatients((prevPatients) => {
        const newPatients = [...prevPatients, ...patientsData.results];
        // Remove duplicates based on patient ID
        const uniquePatients = newPatients.reduce((acc, current) => {
          if (!acc.find((item) => item.id === current.id)) {
            acc.push(current);
          }
          return acc;
        }, []);
        return uniquePatients;
      });
      setHasMore(patientsData.next !== null);
    } else {
      setHasMore(false);
    }
  }, [patientsData]);

  useEffect(() => {
    // Fetch initial data when component mounts
    if (!searchTerm) {
      setAllPatients([]);
      setPage(1);
      setHasMore(true);
      refetch();
    }
  }, []);

  useEffect(() => {
    if (searchTerm !== "") {
      setAllPatients([]);
      setPage(1);
      setHasMore(true);
      refetch();
    } else {
      // When searchTerm is cleared, fetch initial data
      setAllPatients([]);
      setPage(1);
      setHasMore(true);
      refetch();
    }
  }, [searchTerm, refetch]);

  const [input, setInput] = useState({
    date: getCurrentDate(),
    patient: null,
    items: [],
    total_price: 0,
    type: "",
    operation_room: "",
  });

  const [formErrors, setFormErrors] = useState({});
  const [autocompleteInputValue, setAutocompleteInputValue] = useState("");

  const autocompleteRef = useRef(null);
  const quantityRefs = useRef([]);
  const priceRefs = useRef([]);

  const navigate = useNavigate();

  useEffect(() => {
    if (resetForm) {
      setInput({
        date: getCurrentDate(),
        patient: null,
        items: [],
        total_price: 0,
        type: "",
        operation_room: "",
      });
      setFormErrors({});
      setPatientInputValue("");
      setAllPatients([]);
      setPage(1);
      setHasMore(true);
    }
  }, [resetForm]);

  const handleChangeItemField = (index, e) => {
    const { name, value } = e.target;
    const items = [...input.items];

    items[index][name] = value ? parseFloat(value) || 0 : 0;

    if (name === "quantity" || name === "sell_price") {
      const quantity = parseFloat(items[index].quantity) || 0;
      const sellPrice = parseFloat(items[index].sell_price) || 0;
      items[index].total_price = parseFloat((quantity * sellPrice).toFixed(2));
    }

    setInput({ ...input, items });
    updateTotalBill(items);
  };

  const handleAddNewItem = (medicine) => {
    if (medicine) {
      const sellPrice = parseFloat(medicine.sell_price) || 0;
      const newItem = {
        stock_medicine: medicine.id,
        name: medicine.medicine_display,
        quantity: 1,
        sell_price: sellPrice,
        total_price: parseFloat(sellPrice.toFixed(2)),
        available_stock: medicine.quantity,
      };

      const newItems = [...input.items, newItem];
      setInput((prevInput) => ({
        ...prevInput,
        items: newItems,
      }));
      updateTotalBill(newItems);
      setAutocompleteInputValue("");
      if (autocompleteRef.current) {
        autocompleteRef.current.focus();
      }
    }
  };

  const handleRemoveItem = (index) => {
    const items = [...input.items];
    items.splice(index, 1);
    setInput({ ...input, items });
    updateTotalBill(items);
  };

  const updateTotalBill = (items) => {
    const totalPrice = items.reduce(
      (sum, item) => sum + (parseFloat(item.total_price) || 0),
      0
    );

    setInput((prevInput) => ({
      ...prevInput,
      total_price: parseFloat(totalPrice.toFixed(2)),
    }));
  };

  const handleSave = (e) => {
    e.preventDefault();
    let isValid = true;
    let errors = {};

    input.items.forEach((item, index) => {
      if (!item.stock_medicine) {
        errors[`stock_medicine_${index}`] = t("pharmacy.medicineRequired");
        isValid = false;
      }
      if (!item.quantity) {
        errors[`quantity_${index}`] = t("pharmacy.quantityRequired");
        isValid = false;
      }
    });

    setFormErrors(errors);
    if (isValid) {
      const payload = {
        date: input.date,
        patient: input.patient?.id,
        type: input?.type,
        operation_room: input?.operation_room,
        items: input.items.map((item) => ({
          stock_medicine: item.stock_medicine,
          quantity: item.quantity,
        })),
      };
      handleAddOrder(payload);
    }
  };

  const handleKeyDown = (index, fieldType, e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (fieldType === "quantity") {
        if (priceRefs.current[index]) {
          priceRefs.current[index].focus();
        }
      } else if (fieldType === "sell_price") {
        if (index < input.items.length - 1) {
          if (quantityRefs.current[index + 1]) {
            quantityRefs.current[index + 1].focus();
          }
        } else {
          if (autocompleteRef.current) {
            autocompleteRef.current.focus();
          }
        }
      }
    }
  };

  // Patient Autocomplete Handlers
  const handlePatientInputChange = (event, newInputValue) => {
    setPatientInputValue(newInputValue);
    debouncedSetSearchTerm(newInputValue); // Use debounced function
  };

  const handlePatientChange = (event, newValue) => {
    setInput({ ...input, patient: newValue });
  };

  const handleScroll = (event) => {
    const { scrollTop, clientHeight, scrollHeight } = event.target;
    if (
      scrollHeight - scrollTop <= clientHeight + 50 &&
      hasMore &&
      !isLoadingPatients
    ) {
      setPage((prevPage) => prevPage + 1);
    }
  };

  // Debounced setSearchTerm
  const debouncedSetSearchTerm = useCallback(
    debounce((value) => {
      setSearchTerm(value);
    }, 300),
    []
  );

  // Debounced medicine search
  const debouncedMedicineSearch = useCallback(
    debounce((value) => {
      if (value && value.length >= 2) {
        searchMedicines(value, 50);
      } else {
        clearMedicineResults();
      }
    }, 300),
    [searchMedicines, clearMedicineResults]
  );

  // Function to force update
  const forceUpdateAutocomplete = () => {
    setForceUpdate((prev) => prev + 1);
  };

  useEffect(() => {
    if (searchTerm === "") {
      forceUpdateAutocomplete();
    }
  }, [searchTerm]);

  return (
    <Paper
      sx={{
        boxShadow: "0px 8px 24px rgba(0, 0, 0, 0.1)",
        borderRadius: "16px",
        mt: 4,
        mb: 4,
        p: 6,
        width: "100%",
        mx: "auto",
      }}
    >
      {/* Go Back Icon and Title */}
      <Box display="flex" alignItems="center" mb={4}>
        <Tooltip title={t("pharmacy.goBack")} arrow>
          <IconButton
            onClick={() => navigate(-1)}
            aria-label={t("pharmacy.goBack")}
            sx={{ mr: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
        </Tooltip>
        <Typography fontWeight={600} variant="h4" color="primary" gutterBottom>
          {t("pharmacy.freeMedicine")}
        </Typography>
      </Box>

      <Grid container spacing={4}>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label={t("pharmacy.orderDate")}
            name="date"
            type="date"
            size="medium"
            variant="outlined"
            InputLabelProps={{ shrink: true }}
            value={input.date}
            onChange={(e) => setInput({ ...input, date: e.target.value })}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Autocomplete
            options={allPatients}
            getOptionLabel={(option) =>
              `${option.firstname} ${option.lastname || ""}  Phone: ${
                option.phone || "N/A"
              })`.trim()
            }
            inputValue={patientInputValue}
            onInputChange={handlePatientInputChange}
            onChange={handlePatientChange}
            value={input.patient}
            ListboxProps={{
              onScroll: handleScroll,
            }}
            loading={isLoadingPatients} // Add the loading prop
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("pharmacy.patient")}
                variant="outlined"
                placeholder={t(
                  "pharmacy.searchPatientPlaceholder",
                  "Search Patient Name"
                )}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <React.Fragment>
                      {isLoadingPatients ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </React.Fragment>
                  ),
                }}
              />
            )}
          />
        </Grid>
      </Grid>

      <Grid container justifyContent={"space-between"} mt={4}>
        <Grid item xs={12} md={5.9}>
          <TextField
            fullWidth
            name="type"
            label="Section"
            variant="outlined"
            value={input.type}
            onChange={(e) => setInput({ ...input, type: e.target.value })}
            sx={{ backgroundColor: "#fff", borderRadius: 2 }}
          />
        </Grid>
        <Grid item xs={12} md={5.9}>
          <TextField
            fullWidth
            name="operation_room"
            label="Operation Room"
            variant="outlined"
            value={input.operation_room}
            onChange={(e) =>
              setInput({ ...input, operation_room: e.target.value })
            }
            sx={{ backgroundColor: "#fff", borderRadius: 2 }}
          />
        </Grid>
      </Grid>

      <Divider sx={{ my: 4 }} />

      {/* Search Medicine Section */}
      <Box mb={4}>
        <Typography variant="subtitle1" fontWeight={500} mb={2}>
          {t("pharmacy.addMedicine")}
        </Typography>
        <Autocomplete
          ref={autocompleteRef}
          value={null}
          inputValue={autocompleteInputValue}
          onInputChange={(event, newInputValue) => {
            setAutocompleteInputValue(newInputValue);
            // Clear previous results immediately when user types
            if (newInputValue.length < 2) {
              clearMedicineResults();
            }
            // Trigger backend search
            debouncedMedicineSearch(newInputValue);
          }}
          onChange={(event, value) => {
            handleAddNewItem(value);
            setAutocompleteInputValue("");
            clearMedicineResults();
          }}
          options={
            autocompleteInputValue.length >= 2 ? medicineSuggestions || [] : []
          }
          loading={isSearchingMedicines}
          filterOptions={(x) => x}
          getOptionLabel={(option) => option.medicine_display}
          renderOption={(props, option) => (
            <Box
              component="li"
              {...props}
              sx={{
                padding: 1,
                display: "flex",
                alignItems: "center",
                gap: 2,
                backgroundColor: "#f9f9f9",
                borderRadius: 1,
                "&:hover": {
                  backgroundColor: "#eef2f5",
                },
              }}
            >
              <Typography
                variant="body1"
                fontWeight={600}
                sx={{ whiteSpace: "nowrap", color: "text.primary" }}
              >
                {option.medicine_display}
              </Typography>
              <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
              <Typography
                variant="body2"
                sx={{
                  whiteSpace: "nowrap",
                  color: "text.secondary",
                }}
              >
                <strong>{t("pharmacy.category")}:</strong>{" "}
                {option.category_display}
              </Typography>
              <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
              <Typography
                variant="body2"
                sx={{
                  whiteSpace: "nowrap",
                  color: "text.secondary",
                }}
              >
                <strong>{t("pharmacy.price")}:</strong> {option.sell_price}
              </Typography>
              <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
              <Typography
                variant="body2"
                sx={{
                  whiteSpace: "nowrap",
                  color: "text.secondary",
                }}
              >
                <strong>{t("pharmacy.expiryDate")}:</strong>{" "}
                {option.expiry_date}
              </Typography>
            </Box>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t("pharmacy.searchMedicine")}
              variant="outlined"
              placeholder={t("pharmacy.searchMedicinePlaceholder")}
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
                endAdornment: (
                  <>
                    {isSearchingMedicines ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
              sx={{
                backgroundColor: "#fff",
                borderRadius: 2,
              }}
            />
          )}
          noOptionsText={
            isSearchingMedicines
              ? t("pharmacy.searching")
              : autocompleteInputValue.length >= 2
              ? t("pharmacy.noMedicinesFound")
              : t("pharmacy.typeToSearch")
          }
        />
      </Box>

      {/* Medicine List Table */}
      <TableContainer component={Paper} elevation={1} sx={{ mb: 4 }}>
        <Table>
          <TableHead sx={{ backgroundColor: "#eef2f5" }}>
            <TableRow>
              <TableCell>{t("pharmacy.medicineName")}</TableCell>
              <TableCell align="center">
                {t("pharmacy.availableStock")}
              </TableCell>
              <TableCell align="center">{t("pharmacy.quantity")}</TableCell>
              <TableCell align="center">{t("pharmacy.price")}</TableCell>
              <TableCell align="center">{t("pharmacy.totalPrice")}</TableCell>
              <TableCell align="center">{t("pharmacy.action")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody sx={{ bgcolor: "#ccfcfc" }}>
            {input.items.map((item, index) => (
              <TableRow key={index} hover>
                <TableCell>{item.name}</TableCell>
                <TableCell align="center">{item.available_stock}</TableCell>
                <TableCell align="center">
                  <TextField
                    inputRef={(el) => (quantityRefs.current[index] = el)}
                    name="quantity"
                    type="number"
                    size="small"
                    variant="outlined"
                    value={item.quantity}
                    onChange={(e) => handleChangeItemField(index, e)}
                    onKeyDown={(e) => handleKeyDown(index, "quantity", e)}
                    sx={{ backgroundColor: "#fff", borderRadius: 2 }}
                  />
                </TableCell>
                <TableCell align="center">
                  <TextField
                    inputRef={(el) => (priceRefs.current[index] = el)}
                    name="sell_price"
                    type="number"
                    size="small"
                    variant="outlined"
                    value={item.sell_price}
                    onChange={(e) => handleChangeItemField(index, e)}
                    onKeyDown={(e) => handleKeyDown(index, "sell_price", e)}
                    sx={{ backgroundColor: "#fff", borderRadius: 2 }}
                  />
                </TableCell>
                <TableCell align="center">
                  <Typography fontWeight={600}>{item.total_price}</Typography>
                </TableCell>
                <TableCell align="center">
                  <Tooltip title={t("pharmacy.removeItem")} arrow>
                    <IconButton
                      color="error"
                      onClick={() => handleRemoveItem(index)}
                    >
                      <RemoveCircleOutline />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Save Button and Total Price Display */}
      <Stack direction="row" justifyContent="flex-end" spacing={2}>
        <TextField
          size="small"
          label={t("pharmacy.totalPrice")}
          type="number"
          value={input.total_price}
          disabled
          sx={{ width: 200, backgroundColor: "#fff", borderRadius: 2 }}
        />
        <Button
          size="small"
          onClick={handleSave}
          variant="contained"
          disabled={loading}
          sx={{
            fontWeight: 700,
            backgroundColor: "primary.main",
            "&:hover": {
              backgroundColor: "primary.dark",
            },
          }}
        >
          {loading ? t("pharmacy.saving") : t("pharmacy.savePrescription")}
        </Button>
      </Stack>
    </Paper>
  );
};

export default FreeMedicine;
